import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { BiddingSpotMarketKomaPrices } from '@ebalance/proto/bffuser/v1/bidding_spot_market_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import {
  AREAS,
  type KomaRow,
  type PriceSeriesCode,
} from '~/constants/spotPrice'
import { decimalToNumber } from '~/models/decimal'
import { komaNumberToTime, komaTimeRange } from '~/models/komaToTime'

/** BFF のエリアコード → 画面の系列コード (system 以外の 9 エリア)。 */
const SERIES_BY_AREA_CODE: Partial<Record<AreaCode, PriceSeriesCode>> = {
  [AreaCode.HOKKAIDO]: 'hokkaido',
  [AreaCode.TOHOKU]: 'tohoku',
  [AreaCode.TOKYO]: 'tokyo',
  [AreaCode.CHUBU]: 'chubu',
  [AreaCode.HOKURIKU]: 'hokuriku',
  [AreaCode.KANSAI]: 'kansai',
  [AreaCode.CHUGOKU]: 'chugoku',
  [AreaCode.SHIKOKU]: 'shikoku',
  [AreaCode.KYUSHU]: 'kyushu',
}

function protoDateToYmd(d: ProtoDate | undefined): string {
  if (d === undefined) return ''
  const mm = String(d.month).padStart(2, '0')
  const dd = String(d.day).padStart(2, '0')
  return `${String(d.year).padStart(4, '0')}-${mm}-${dd}`
}

/**
 * BFF の約定価格 (受渡日 × コマ) を画面の行にする。
 *
 * - 価格は Decimal を number にする。 BFF が返していない系列 (system_price が unset・
 *   area_prices に無いエリア) は **null** のまま (0 にしない)。
 * - 並びは受渡日の昇順・コマ番号の昇順 (BFF の並びに頼らない)。
 * - 画面が知らないエリアコード (沖縄・未指定) は載せない。
 */
export function toKomaRows(
  komaPrices: readonly BiddingSpotMarketKomaPrices[],
): KomaRow[] {
  const rows = komaPrices.map((k): KomaRow => {
    const date = protoDateToYmd(k.deliveryDate)
    const time = komaNumberToTime(k.timeCode)
    const row = {
      date,
      koma: k.timeCode,
      time,
      timeRange: komaTimeRange(time),
      x: `${date} ${time}`,
      ...Object.fromEntries(AREAS.map((a) => [a.code, null] as const)),
    } as KomaRow
    row.system = decimalToNumber(k.systemPrice) ?? null
    for (const a of k.areaPrices) {
      const series = SERIES_BY_AREA_CODE[a.areaCode]
      if (series !== undefined) row[series] = decimalToNumber(a.price) ?? null
    }
    return row
  })
  return rows.sort((a, b) =>
    a.date === b.date ? a.koma - b.koma : a.date < b.date ? -1 : 1,
  )
}

/**
 * チャートの Y 軸ドメイン (全系列・全行の最小値の floor と最大値の ceil)。
 * 表示系列を切り替えてもスケールが暴れないよう、 表示中の系列でなく全系列から出す。
 * 価格が 1 つも無ければ undefined (軸は自動)。
 */
export function chartYDomain(
  rows: readonly KomaRow[],
): [number, number] | undefined {
  let min = Infinity
  let max = -Infinity
  for (const row of rows) {
    for (const a of AREAS) {
      const v = row[a.code]
      if (v === null) continue
      if (v < min) min = v
      if (v > max) max = v
    }
  }
  return min === Infinity ? undefined : [Math.floor(min), Math.ceil(max)]
}
